





























import Vue from "vue";

import DictionarySentenceModal from "@tt-app/components/exercise/SingleSentenceReading/DictionarySentenceModal.vue";
import { Word } from "@tt-app/types";

interface Data {
  intonationCheck: any;
  modal: {
    open: boolean;
  };
  selectedWord?: Word;
}

interface Methods {
  hideModal: () => Promise<void>;
  selectWord: (clickedWord: string) => void;
  setIntonationCheck: (clickedWordIndex: number) => void;
  showModal: (event: MouseEvent) => void;
}

interface Computed {
  definedWords: any;
  highlights: any;
  splitSentence: {
    word: string;
    highlight: boolean;
  }[];
}

interface Props {
  currentProgress: number;
  dictionaryOnly: boolean;
  disabled: boolean;
  displayIntonationCheck: boolean;
  evaluation: any;
  logId: number;
  sentence: string;
  exampleAudioButtonClickHandler: () => void;
  status: string;
  words: Word[];
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "DictionarySentence",
  components: {
    DictionarySentenceModal
  },
  props: {
    currentProgress: {
      type: Number,
      required: true
    },
    dictionaryOnly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    displayIntonationCheck: {
      type: Boolean,
      default: false
    },
    evaluation: {
      type: Object,
      default: () => ({})
    },
    logId: {
      type: Number,
      default: undefined
    },
    exampleAudioButtonClickHandler: {
      type: Function,
      required: true
    },
    sentence: {
      type: String,
      required: true
    },
    status: {
      type: String,
      default: ""
    },
    words: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      intonationCheck: undefined,
      modal: {
        open: false
      },
      selectedWord: undefined
    };
  },

  computed: {
    definedWords() {
      return this.words.map((word: any) => {
        return word.sentenceSpelling.toLowerCase();
      });
    },
    highlights() {
      if (!this.evaluation.results || !this.displayIntonationCheck) return [];

      return this.evaluation.results.elements
        .map((element: any, index: number) => {
          const composition = element.composition[0];
          return composition.pron_is_evaluated && !composition.pron ? index : undefined;
        })
        .filter(Boolean);
    },
    splitSentence() {
      const words = this.sentence.split(" ");

      return words.map((word: any, index: number) => {
        const defined = this.definedWords.includes(word.replace(/[^\w\s'-]|_/g, "").toLowerCase());
        const highlight = this.highlights.includes(index);

        return {
          word,
          defined,
          highlight
        };
      });
    }
  },

  methods: {
    async hideModal() {
      this.modal.open = false;

      this.selectedWord = undefined;
      this.intonationCheck = undefined;
      await this.$nextTick();

      this.selectedWord = undefined;
    },

    selectWord(clickedWord) {
      const foundWord = this.words.find((word: any) => {
        return word.sentenceSpelling.toLowerCase() === clickedWord.toLowerCase();
      });

      this.selectedWord = foundWord;
    },

    setIntonationCheck(clickedWordIndex) {
      if (!this.selectedWord) return;

      const evaluation = this.evaluation.results?.elements[clickedWordIndex].composition[0];

      this.intonationCheck = {
        user: {
          ipaAccuracy: this.selectedWord.ipa.map((ipa: string, index: number) => {
            return {
              text: ipa,
              isCorrect: evaluation && evaluation.phones[index][1] > 0.6
            };
          }),
          stressedSyllables: {
            end: evaluation?.stress_syllable_end_idx,
            start: evaluation?.stress_syllable_start_idx
          }
        },
        example: {
          stressedSyllables: {
            end: evaluation?.dict_stress_syllable_end_idx,
            start: evaluation?.dict_stress_syllable_start_idx
          }
        }
      };
    },

    async showModal(event) {
      if (event.target instanceof HTMLElement) {
        const clickedWord = event.target.innerText.replace(/[^\w\s'-]|_/g, "");

        const clickedWordIndex = Number(event.target.dataset.wordIndex);

        this.selectWord(clickedWord);
        this.setIntonationCheck(clickedWordIndex);

        await this.$nextTick();

        this.modal.open = true;
      }
    }
  }
});
